import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Box, Typography, Button, Alert } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onReset?: () => void;
}

export const RouteErrorBoundary: React.FC<RouteErrorBoundaryProps> = ({
  children,
  fallback,
  onReset,
}) => {
  const navigate = useNavigate();

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      navigate(0);
    }
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleError = (_error: unknown) => {
    enqueueSnackbar("Error inesperado. Por favor, intenta de nuevo", {
      variant: "error",
      autoHideDuration: 4000,
    });
  };

  const DefaultFallback = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 4,
        textAlign: "center",
      }}
    >
      <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
        Algo salió mal al cargar esta página
      </Alert>

      <Typography variant="h6" gutterBottom>
        Error inesperado
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 4, maxWidth: 500 }}
      >
        Hemos experimentado un error al cargar el contenido. Por favor, intenta
        recargar la página o regresa al inicio.
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
        >
          Recargar página
        </Button>

        <Button
          variant="outlined"
          startIcon={<HomeIcon />}
          onClick={handleGoHome}
        >
          Ir al inicio
        </Button>
      </Box>
    </Box>
  );

  const FallbackComponent = () => fallback || <DefaultFallback />;

  return (
    <ErrorBoundary
      FallbackComponent={FallbackComponent}
      onError={handleError}
      onReset={handleReset}
    >
      {children}
    </ErrorBoundary>
  );
};
